import React, { useState, useEffect, useCallback } from 'react';
import {
  getJournalEntries,
  createJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  getJournalStats,
} from '../../api/client';

const MOOD_OPTIONS    = ['bullish', 'bearish', 'neutral'];
const OUTCOME_OPTIONS = ['win', 'loss', 'breakeven'];

const OUTCOME_COLOR = { win: 'var(--green)', loss: 'var(--red)', breakeven: 'var(--yellow)' };
const MOOD_EMOJI    = { bullish: '🐂', bearish: '🐻', neutral: '😐' };

// ── Entry Form ────────────────────────────────────────────────────────────────
function EntryForm({ initial = null, onSave, onCancel }) {
  const [title,   setTitle]   = useState(initial?.title   || '');
  const [notes,   setNotes]   = useState(initial?.notes   || '');
  const [tags,    setTags]    = useState(initial?.tags    || '');
  const [mood,    setMood]    = useState(initial?.mood    || '');
  const [outcome, setOutcome] = useState(initial?.outcome || '');
  const [tradeId, setTradeId] = useState(initial?.trade_id || '');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        notes: notes.trim() || null,
        tags:  tags.trim()  || null,
        mood:  mood  || null,
        outcome: outcome || null,
        trade_id: tradeId ? parseInt(tradeId) : null,
      };
      if (initial) {
        await updateJournalEntry(initial.id, payload);
      } else {
        await createJournalEntry(payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entry');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box' };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Title *</label>
          <input
            className="input" style={inputStyle}
            placeholder="e.g. NIFTY breakout trade — missed stop"
            value={title} onChange={e => setTitle(e.target.value)} required
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Mood</label>
          <select className="input" style={inputStyle} value={mood} onChange={e => setMood(e.target.value)}>
            <option value="">— Select —</option>
            {MOOD_OPTIONS.map(m => <option key={m} value={m}>{MOOD_EMOJI[m]} {m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Outcome</label>
          <select className="input" style={inputStyle} value={outcome} onChange={e => setOutcome(e.target.value)}>
            <option value="">— Select —</option>
            {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Tags (comma-separated)</label>
          <input
            className="input" style={inputStyle}
            placeholder="breakout, nifty, options"
            value={tags} onChange={e => setTags(e.target.value)}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Linked Trade ID (optional)</label>
          <input
            className="input" style={inputStyle}
            type="number" placeholder="Trade #"
            value={tradeId} onChange={e => setTradeId(e.target.value)}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea
            className="input"
            style={{ ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="What happened? What did you learn? What would you do differently?"
            value={notes} onChange={e => setNotes(e.target.value)}
          />
        </div>
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : initial ? 'Update Entry' : 'Add Entry'}
        </button>
        {onCancel && (
          <button className="btn btn-ghost" type="button" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

// ── Entry Card ────────────────────────────────────────────────────────────────
function EntryCard({ entry, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [busy,     setBusy]     = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this journal entry?')) return;
    setBusy(true);
    try { await deleteJournalEntry(entry.id); onRefresh(); } finally { setBusy(false); }
  };

  const tagList = entry.tags ? entry.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {editing ? (
        <EntryForm
          initial={entry}
          onSave={() => { setEditing(false); onRefresh(); }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{entry.title}</span>
                {entry.outcome && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                    background: OUTCOME_COLOR[entry.outcome] + '22',
                    color: OUTCOME_COLOR[entry.outcome],
                    border: `1px solid ${OUTCOME_COLOR[entry.outcome]}44`,
                  }}>
                    {entry.outcome.toUpperCase()}
                  </span>
                )}
                {entry.mood && (
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {MOOD_EMOJI[entry.mood]} {entry.mood}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: tagList.length ? 8 : 0 }}>
                {new Date(entry.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                {entry.trade_id && <span style={{ marginLeft: 10 }}>📎 Trade #{entry.trade_id}</span>}
              </div>
              {tagList.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {tagList.map(tag => (
                    <span key={tag} style={{
                      fontSize: 11, padding: '1px 7px', borderRadius: 99,
                      background: 'var(--bg)', border: '1px solid var(--border)',
                      color: 'var(--muted)',
                    }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
              <button
                className="btn btn-ghost"
                style={{ fontSize: 11, padding: '3px 8px' }}
                onClick={() => setExpanded(x => !x)}
              >
                {expanded ? 'Collapse' : 'Notes'}
              </button>
              <button
                className="btn btn-ghost"
                style={{ fontSize: 11, padding: '3px 8px' }}
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
              <button
                className="btn btn-ghost"
                style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red)' }}
                onClick={handleDelete}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          </div>
          {expanded && entry.notes && (
            <div style={{
              marginTop: 14, padding: '12px 14px',
              background: 'var(--bg)', borderRadius: 6,
              fontSize: 13, color: 'var(--text)',
              whiteSpace: 'pre-wrap', lineHeight: 1.6,
              borderLeft: '3px solid var(--accent)',
            }}>
              {entry.notes}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar({ stats }) {
  if (!stats) return null;
  const { total, outcomes, top_tags } = stats;
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
      <div className="stat-card" style={{ minWidth: 100 }}>
        <div className="stat-label">Total Entries</div>
        <div className="stat-value">{total}</div>
      </div>
      {OUTCOME_OPTIONS.map(o => (
        <div key={o} className="stat-card" style={{ minWidth: 80 }}>
          <div className="stat-label">{o.charAt(0).toUpperCase() + o.slice(1)}s</div>
          <div className="stat-value" style={{ color: OUTCOME_COLOR[o] }}>{outcomes?.[o] || 0}</div>
        </div>
      ))}
      {top_tags?.slice(0, 5).map(({ tag, count }) => (
        <div key={tag} className="stat-card" style={{ minWidth: 80 }}>
          <div className="stat-label">#{tag}</div>
          <div className="stat-value" style={{ fontSize: 18 }}>{count}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TradeJournal() {
  const [entries,    setEntries]    = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [filterOutcome, setFilterOutcome] = useState('');
  const [filterTag,     setFilterTag]     = useState('');
  const [page,       setPage]       = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const params = { page, page_size: 20 };
      if (filterOutcome) params.outcome = filterOutcome;
      if (filterTag)     params.tag     = filterTag;

      const [eRes, sRes] = await Promise.all([
        getJournalEntries(params),
        getJournalStats(),
      ]);
      setEntries(eRes.data.entries || []);
      setStats(sRes.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, filterOutcome, filterTag]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>📓 Trade Journal</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(x => !x)}>
          {showForm ? '✕ Cancel' : '+ New Entry'}
        </button>
      </div>

      {/* Stats */}
      <StatsBar stats={stats} />

      {/* New Entry Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>New Journal Entry</div>
          <EntryForm
            onSave={() => { setShowForm(false); fetchData(); }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Filter:</span>
        <select
          className="input"
          style={{ width: 140 }}
          value={filterOutcome}
          onChange={e => { setFilterOutcome(e.target.value); setPage(1); }}
        >
          <option value="">All outcomes</option>
          {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <input
          className="input"
          style={{ width: 160 }}
          placeholder="Filter by tag…"
          value={filterTag}
          onChange={e => { setFilterTag(e.target.value); setPage(1); }}
        />
        {(filterOutcome || filterTag) && (
          <button className="btn btn-ghost" style={{ fontSize: 12 }}
            onClick={() => { setFilterOutcome(''); setFilterTag(''); setPage(1); }}>
            Clear
          </button>
        )}
      </div>

      {/* Entries */}
      {loading ? (
        <div style={{ color: 'var(--muted)' }}>Loading journal…</div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', padding: 32 }}>
          No entries yet. Add your first trade journal entry above.
        </div>
      ) : (
        <>
          {entries.map(entry => (
            <EntryCard key={entry.id} entry={entry} onRefresh={fetchData} />
          ))}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn btn-ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ fontSize: 13, color: 'var(--muted)', padding: '6px 0' }}>Page {page}</span>
            <button className="btn btn-ghost" disabled={entries.length < 20} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}
